import {
  type AssignedUserResource,
  OutletAssignmentsController_list_v1,
  type OutletResource,
  OutletsController_list_v1,
  type UserResource,
  UsersController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { Switch } from '@repo/ui/components/switch';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconCircleCheck, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';

import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { EditUserDialog } from './EditUserDialog';
import { NewUserDialog } from './NewUserDialog';
import {
  isDeactivated,
  isLockedOut,
  labelForRole,
  labelForVia,
  USER_ROLE_ITEMS,
  USER_ROLES,
  type UserRole,
} from './user-roles';

/**
 * The park's people: who can sign in, what they may do, and how to add somebody.
 *
 * **Two filters, and they read from two different routes.** The role filter is a
 * query parameter on `GET /v1/users`, so it is asked of the API rather than
 * applied to a page of fifty. The outlet filter is not: `GET /v1/users` takes no
 * outlet parameter in the shipped document, and the question "who works at this
 * outlet" is answered by `GET /v1/outlets/{id}/users` — the other direction of
 * the same many-to-many. Choosing an outlet therefore switches which operation
 * this screen reads, which is why `ListState` carries the rows and not a flag.
 *
 * The ticket describes an outlet filter on `GET /v1/users`; the contract does
 * not have one. Reported in the PR body. This is the shipped way to ask the
 * question and it answers it more completely — the roster names owners and
 * managers with `via=ROLE`, who reach every outlet without an assignment row
 * (FR-111) and would be missing from any filter built on assignment rows alone.
 *
 * **The roster route omits deactivated people and takes no parameter to include
 * them**, so while an outlet is chosen the deactivated toggle is disabled and
 * says why. A control that silently stopped working would read as a bug in the
 * toggle; a disabled one with a sentence beside it reads as the boundary it is.
 *
 * **Nothing here renders a credential, and nothing can.** No response in this
 * epic carries `password_hash` or `pin_hash` — the server's controller row type
 * omits them, so returning one is a compile error there rather than something a
 * review has to catch here. What this screen does show is whether somebody is
 * still holding a credential *somebody else chose*, which is the fact an owner
 * acts on.
 *
 * **The name is the handle.** A row carries no buttons of its own — the screen
 * has one accent and it is on "New person" — so a person is opened the way a
 * record is opened anywhere in this app, by their name. That holds for a
 * deactivated one too: deactivation is not deletion, so `EditUserDialog` opens
 * them like anybody else and offers the way back.
 *
 * **Ten people today; fifty must need no code change.** Both lists are read
 * whole through `collectPages`, so the API's fifty-row default page does not
 * quietly become this screen's ceiling.
 */

/** "Everybody", as a value a `Select` can hold — it cannot hold `undefined`. */
const EVERY_ROLE = 'ALL';
const EVERY_OUTLET = 'ALL';

/** A row as this screen renders it: `via` only when an outlet was chosen. */
type PersonRow = UserResource & { readonly via?: AssignedUserResource['via'] };

/** Every state the list can be in. One value, so two of them cannot coexist. */
type ListState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly people: readonly PersonRow[] }
  | { readonly kind: 'failed'; readonly error: unknown };

/** Kept next to the header cells below, so the two cannot drift. */
const COLUMNS_WITHOUT_VIA = 4;

/**
 * What just happened to somebody, said once above the list.
 *
 * One shape for every outcome rather than one flag per action. Five booleans
 * would allow two banners at once — a rename and a deactivation cannot both be
 * the last thing that happened — and the staff code is carried alongside the
 * sentence because it is what an owner reads back to the person it concerns.
 */
interface Notice {
  readonly staffCode: string;
  readonly text: string;
}

export function Users() {
  const client = useApiClient();

  const [role, setRole] = useState<UserRole | typeof EVERY_ROLE>(EVERY_ROLE);
  const [outletId, setOutletId] = useState<string>(EVERY_OUTLET);
  const [includeDeactivated, setIncludeDeactivated] = useState(false);

  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  // What the trigger reads. Base UI takes the label from here, not from the
  // rendered items, so this has to carry the filter's own "every outlet" row
  // as well as the real ones.
  const outletItems = [
    { label: 'Every outlet', value: EVERY_OUTLET },
    ...outlets.map((option) => ({
      label: `${option.name} (${option.code})`,
      value: option.id,
    })),
  ];
  const roleFilterItems = [{ label: 'Every role', value: EVERY_ROLE }, ...USER_ROLE_ITEMS];
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserResource | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  // Bumping this re-runs the load. A counter rather than a boolean, so two
  // retries in a row are two distinct values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const reload = useCallback(() => setReloads((count) => count + 1), []);

  const filteringByOutlet = outletId !== EVERY_OUTLET;

  // The outlets that populate the filter. Read once and separately from the
  // people, because a failure to read them is not a failure to read the list:
  // the filter is simply left with only "Every outlet" in it, and the screen
  // still does its main job.
  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((found) => {
        if (live) setOutlets(found);
      })
      .catch(() => {
        // Deliberately swallowed. See above: this is a filter's contents, not
        // the screen's content, and surfacing it would put a second error panel
        // on a page whose list loaded perfectly well.
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    // Two guards, not one. `controller` stops the request itself the moment a
    // filter moves — there is no reason to leave it running — and `live` stops a
    // response that was already in flight from being written into state after
    // this effect was torn down. Without the second, changing a filter twice
    // quickly can land the first answer on top of the second and leave the
    // screen showing one outlet's roster under another outlet's name.
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    const load = filteringByOutlet
      ? collectPages<AssignedUserResource>((offset, limit) =>
          client.request(OutletAssignmentsController_list_v1, {
            path: { id: outletId },
            query: { offset, limit },
            signal: controller.signal,
          }),
        ).then((roster) =>
          // The roster route takes no role parameter, so this one filter is
          // applied here rather than asked for. It is the only client-side
          // filtering on this screen and it is bounded by an outlet's staff
          // rather than by the park's.
          role === EVERY_ROLE ? roster : roster.filter((person) => person.role === role),
        )
      : collectPages<UserResource>((offset, limit) =>
          client.request(UsersController_list_v1, {
            // Each option is sent only when it narrows something. The API
            // defaults to every role and to active people only, and saying so
            // explicitly would work — but the query string is what appears in a
            // log when somebody is working out what a screen asked for, and a
            // screen that only ever asks for the default is more honest read
            // that way.
            query: {
              offset,
              limit,
              ...(role === EVERY_ROLE ? {} : { role }),
              ...(includeDeactivated ? { includeDeactivated: true } : {}),
            },
            signal: controller.signal,
          }),
        );

    load
      .then((people) => {
        if (live) setState({ kind: 'loaded', people });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, role, outletId, filteringByOutlet, includeDeactivated, reloads]);

  // The "how they reach it" column earns its place only when it can
  // discriminate, which is only when the list is an outlet's roster. Everywhere
  // else every row would read the same, which is a column staff learn to stop
  // reading.
  const columns = filteringByOutlet ? COLUMNS_WITHOUT_VIA + 1 : COLUMNS_WITHOUT_VIA;

  return (
    <Screen
      title="People"
      description="Everybody who signs in — at a desk, at a gate or at a till. A staff code identifies one person for as long as the park keeps records, so it is issued once and never reissued."
      action={
        // The screen's one primary action, and the only accent on it. Rows
        // carry no buttons of their own for the same reason.
        <Button onClick={() => setCreating(true)}>
          <IconPlus />
          New person
        </Button>
      }
      aside={
        <>
          {state.kind === 'loaded' && (
            <ScreenFact label="Showing">
              <span className="font-mono numeric">{state.people.length}</span>{' '}
              {state.people.length === 1 ? 'person' : 'people'}
            </ScreenFact>
          )}

          <div className="space-y-2">
            <Label htmlFor="filter-role" className="text-sm text-muted-foreground">
              Role
            </Label>
            <Select
              value={role}
              // `USER_ROLE_ITEMS` plus this filter's own "every role" row, which
              // is not a role and so is not in the shared map.
              items={roleFilterItems}
              onValueChange={(next) => setRole(next as UserRole | typeof EVERY_ROLE)}
            >
              <SelectTrigger id="filter-role" aria-label="Role" className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={EVERY_ROLE}>Every role</SelectItem>
                  {USER_ROLES.map((option) => (
                    <SelectItem key={option} value={option}>
                      {labelForRole(option)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filter-outlet" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            {/* `next ?? EVERY_OUTLET` because Base UI's `onValueChange` is
                typed to deliver `null` when a selection is cleared. Nothing on
                this trigger offers to clear it, but the filter's "no outlet"
                answer is a value here rather than an absence, and letting
                `null` reach the state would make it one of two. */}
            <Select
              value={outletId}
              // Without `items` the trigger shows the raw value, which for every
              // option but the first is an outlet id.
              items={outletItems}
              onValueChange={(next) => setOutletId(next ?? EVERY_OUTLET)}
            >
              <SelectTrigger id="filter-outlet" aria-label="Outlet" className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* The group is what pads the popup. */}
                <SelectGroup>
                  <SelectItem value={EVERY_OUTLET}>Every outlet</SelectItem>
                  {outlets.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name} ({option.code})
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="show-deactivated"
              aria-labelledby="show-deactivated-label"
              checked={includeDeactivated && !filteringByOutlet}
              disabled={filteringByOutlet}
              onCheckedChange={(checked) => setIncludeDeactivated(checked)}
            />
            <Label
              id="show-deactivated-label"
              htmlFor="show-deactivated"
              className="cursor-pointer text-sm"
            >
              Show deactivated people
            </Label>
          </div>
        </>
      }
    >
      {/* Said next to the control it explains rather than as an error, because
          nothing has gone wrong: it is the shape of the route being read. */}
      {filteringByOutlet && (
        <p className="-mt-3 text-sm text-muted-foreground">
          The roster of an outlet lists only active people, so deactivated people cannot be shown
          alongside it. Choose <span className="text-foreground">Every outlet</span> to see them.
        </p>
      )}

      {notice !== null && (
        <Alert
          // `status` and not the component's default `alert`: a success is not
          // an interruption, and this is announced politely rather than cutting
          // across whatever the reader was doing.
          role="status"
        >
          <IconCircleCheck />
          <AlertDescription>
            <span className="font-mono text-foreground numeric">{notice.staffCode}</span> —{' '}
            {notice.text}
          </AlertDescription>
        </Alert>
      )}

      <ScreenTable label="People">
        <TableHeader>
          <TableRow>
            <TableHead className="w-36">Staff code</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="w-40">Role</TableHead>
            <TableHead className="w-64">Standing</TableHead>
            {filteringByOutlet && <TableHead className="w-32">Reaches it</TableHead>}
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={columns} />}

        {state.kind === 'failed' && (
          <TableNotice columns={columns}>
            <LoadFailed error={state.error} onRetry={reload} />
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (state.people.length === 0 ? (
            <TableNotice columns={columns}>
              <EmptyState
                title="Nobody here yet"
                description={emptyDescription({
                  filteringByOutlet,
                  filteringByRole: role !== EVERY_ROLE,
                })}
              />
            </TableNotice>
          ) : (
            <TableBody>
              {state.people.map((person) => (
                <PersonTableRow
                  key={person.id}
                  person={person}
                  showVia={filteringByOutlet}
                  onOpen={() => setEditing(person)}
                />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      <NewUserDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(person) => {
          setCreating(false);
          setNotice({
            staffCode: person.staffCode,
            text: person.mustChangeCredential
              ? `${person.displayName} has been created, and must replace the credential you set before they can do anything else.`
              : `${person.displayName} has been created. They have no credential yet, so set one before they can sign in.`,
          });

          // Re-read rather than splicing the new person into the list. The API
          // decides the order and whether anything else moved while this form
          // was open, and a locally patched list is a second answer to a
          // question the server has already answered.
          reload();
        }}
      />

      {/* Mounted only while somebody is open, so the next person opened starts
          from their own values rather than from the last one's. */}
      {editing !== null && (
        <EditUserDialog
          user={editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          onUpdated={(person) => {
            setEditing(null);
            setNotice({
              staffCode: person.staffCode,
              text: person.mustChangeCredential
                ? `${person.displayName} has been updated, and must replace the credential you set before they can do anything else. Any session they had open has ended.`
                : `${person.displayName} has been updated.`,
            });
            reload();
          }}
          onDeactivated={(person) => {
            setEditing(null);
            setNotice({
              staffCode: person.staffCode,
              // The promise repeated where the confirmation made it. An owner
              // who has just ended somebody's employment is the reader most
              // likely to want it a second time.
              text: `${person.displayName} can no longer sign in. Everything they did keeps their name on it, and their staff code stays theirs for ever. Turn on Show deactivated people to find them again.`,
            });

            // Re-read rather than dropping the row locally. With the toggle off
            // they leave the list, with it on they come back marked — and which
            // of those is right is a question the API has just been asked.
            reload();
          }}
          onReactivated={(person) => {
            setEditing(null);
            setNotice({
              staffCode: person.staffCode,
              text: `${person.displayName} can sign in again, under the same staff code they always had. Their old sessions are not restored, so they sign in afresh.`,
            });
            reload();
          }}
          onLockoutCleared={(person) => {
            setEditing(null);
            setNotice({
              staffCode: person.staffCode,
              text: `The lockout on ${person.displayName} has been cleared. They can try again straight away.`,
            });
            reload();
          }}
        />
      )}
    </Screen>
  );
}

/**
 * What "nobody" means, which depends entirely on what was asked for.
 *
 * A filtered list that comes back empty is not an empty park, and telling
 * somebody to create their first person when they have simply picked a role
 * nobody holds sends them to create a duplicate.
 */
function emptyDescription({
  filteringByOutlet,
  filteringByRole,
}: {
  filteringByOutlet: boolean;
  filteringByRole: boolean;
}): string {
  if (filteringByOutlet) {
    return filteringByRole
      ? 'Nobody in that role can work at that outlet. Owners and managers reach every outlet without being assigned one; everybody else needs an assignment.'
      : 'Nobody can work at that outlet yet. Owners and managers reach every outlet without being assigned one; everybody else is assigned from the outlet itself.';
  }

  return filteringByRole
    ? 'Nobody holds that role. Choose Every role to see the park’s people, or use New person above to add somebody to it.'
    : 'Everybody who signs in needs a person here — the owner, the managers, and the staff at every gate and till. Use New person above to add the first one.';
}

function PersonTableRow({
  person,
  showVia,
  onOpen,
}: {
  person: PersonRow;
  showVia: boolean;
  onOpen: () => void;
}) {
  const gone = isDeactivated(person);

  return (
    // A deactivated row is quieter, not struck through or greyed to the point of
    // being unreadable: it is history that must stay legible, because the whole
    // reason for keeping it reachable is that somebody is looking something up.
    // `text-muted-foreground` is safe here — the row sits on `bg-card`, and it
    // is only the muted-on-muted pairing that misses AA.
    <TableRow className={gone ? 'text-muted-foreground' : undefined}>
      {/* A staff code is read aloud and typed back in, so it gets the mono face
          and tabular figures — the column has to compare cleanly down its
          length. */}
      <TableCell className="font-mono text-foreground numeric">{person.staffCode}</TableCell>
      <TableCell>
        {/* The row's only control, and it carries no chrome: the accent on this
            screen belongs to "New person". Underlined on hover and focus so it
            is discoverable as a control without twenty of them competing with
            the one primary action. */}
        <button
          type="button"
          className="rounded-sm text-left underline-offset-4 outline-none hover:underline focus-visible:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          onClick={onOpen}
        >
          <span className={gone ? undefined : 'text-foreground'}>{person.displayName}</span>
        </button>
      </TableCell>
      <TableCell>{labelForRole(person.role)}</TableCell>
      <TableCell>
        <Standing person={person} />
      </TableCell>

      {showVia && (
        <TableCell>{person.via === undefined ? null : labelForVia(person.via)}</TableCell>
      )}
    </TableRow>
  );
}

/**
 * Where somebody stands, in one cell and one answer.
 *
 * Ordered by what stops them working: deactivated first, then locked out, then
 * a credential they have not replaced. Two of these can be true at once and
 * showing both would make a column of paragraphs; the first one that applies is
 * the one an owner would act on.
 *
 * No badge is `destructive`. Red is for a system that refused something or is
 * about to — deactivating somebody is an ordinary end to an ordinary job, and a
 * lockout is a timer, not a punishment the interface should join in with.
 */
function Standing({ person }: { person: PersonRow }) {
  if (isDeactivated(person)) return <Badge variant="outline">Deactivated</Badge>;

  if (isLockedOut(person)) return <Badge variant="outline">Locked out</Badge>;

  if (person.mustChangeCredential) {
    return <span className="text-foreground">Has not replaced their credential</span>;
  }

  return <span className="text-muted-foreground">Active</span>;
}
